class NodaraProduct extends HTMLElement {
  constructor() {
    super();

    this.initialized = false;

    this.galleryScrollFrame = null;

    /* =====================================================
       STICKY STATE
    ===================================================== */

    this.stickyObserver = null;

    this.stickyFooterObserver = null;

    this.stickyFooter = null;

    this.stickyMainCtaPassed = false;

    this.stickyFooterVisible = false;
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;

    this.cacheElements();
    this.parseVariants();
    this.bindEvents();
    this.syncInitialState();
    this.updateDeliveryDate();
    this.initializeStickyAtc();
    this.initializeProductDetails();
    this.initializeSizeGuides();
    this.initializeCrossSell();
  }

  cacheElements() {
    /* =====================================================
       VARIANTS
    ===================================================== */

    this.variantPicker = this.querySelector(
      '[data-nd-variant-picker]'
    );

    this.variantDataElement = this.querySelector(
      '[data-nd-product-variants]'
    );

    this.optionInputs = [
      ...this.querySelectorAll(
        '[data-nd-option-input]'
      )
    ];

    /* =====================================================
       PRICE
    ===================================================== */

    this.priceWrapper = this.querySelector(
      '[data-nd-price-wrapper]'
    );

    this.priceElement = this.querySelector(
      '[data-nd-price]'
    );

    this.comparePriceElement = this.querySelector(
      '[data-nd-compare-price]'
    );

    this.discountElement = this.querySelector(
      '[data-nd-discount]'
    );

    this.savingElement = this.querySelector(
      '[data-nd-saving]'
    );

    /* =====================================================
       INVENTORY
    ===================================================== */

    this.inventoryElement = this.querySelector(
      '[data-nd-inventory]'
    );

    /* =====================================================
       PRODUCT FORM
    ===================================================== */

    this.productForm = this.querySelector(
      '[data-nd-product-form]'
    );

    this.variantIdInput = this.querySelector(
      '[data-nd-variant-id]'
    );

    this.addToCartButton = this.querySelector(
      '[data-nd-atc]'
    );

    this.addToCartText = this.querySelector(
      '[data-nd-atc-text]'
    );

    this.selectedVariantSummary =
      this.querySelector(
        '[data-nd-selected-variant-summary]'
      );

    this.selectedVariantValue =
      this.querySelector(
        '[data-nd-selected-variant-value]'
      );

    this.addToCartStatus =
      this.querySelector(
        '[data-nd-atc-status]'
      );

    this.addToCartSpinner =
      this.querySelector(
        '[data-nd-atc-spinner]'
      );

    this.productForm =
      this.querySelector(
        '.nd-product-form'
      );

    this.addToCartResetTimer =
      null;

    this.quantityInput = this.querySelector(
      '[data-nd-quantity]'
    );

    this.quantityMinus = this.querySelector(
      '[data-nd-quantity-minus]'
    );

    this.quantityPlus = this.querySelector(
      '[data-nd-quantity-plus]'
    );

    /* =====================================================
       DELIVERY
    ===================================================== */

    this.deliveryElement = this.querySelector(
      '[data-nd-delivery]'
    );

    this.deliveryDateElement = this.querySelector(
      '[data-nd-delivery-date]'
    );

    /* =====================================================
       SPECIFICATIONS
    ===================================================== */

    this.specificationSkuValues = [
      ...this.querySelectorAll(
        '[data-nd-specification-sku-value]'
      )
    ];

    /* =====================================================
       DETAILS
    ===================================================== */

    this.productDetails = [
      ...this.querySelectorAll(
        '[data-nd-product-details]'
      )
    ];

    /* =====================================================
       CROSS SELL
    ===================================================== */

    this.crossSellBlocks = [
      ...this.querySelectorAll(
        '[data-nd-cross-sell]'
      )
    ];

    /* =====================================================
       STICKY
    ===================================================== */

    this.stickyAtc = this.querySelector(
      '[data-nd-sticky-atc]'
    );

    this.stickyButton = this.querySelector(
      '[data-nd-sticky-button]'
    );

    this.stickyButtonText = this.querySelector(
      '[data-nd-sticky-button-text]'
    );

    this.stickyPrice = this.querySelector(
      '[data-nd-sticky-price]'
    );

    this.stickyVariant = this.querySelector(
      '[data-nd-sticky-variant]'
    );

    /*
     * El footer está fuera de <nd-product>,
     * por lo tanto se resuelve desde document.
     *
     * Incluimos varios selectores compatibles
     * con Horizon y con el footer nativo de Shopify.
     */

    this.stickyFooter =
      document.querySelector(
        '.shopify-section-group-footer-group'
      )
      || document.querySelector(
        '[data-section-type="footer"]'
      )
      || document.querySelector(
        'footer'
      );

    /* =====================================================
       DESKTOP GALLERY
    ===================================================== */

    this.gallery = this.querySelector(
      '[data-nd-gallery]'
    );

    this.galleryPrev = this.querySelector(
      '[data-nd-gallery-prev]'
    );

    this.galleryNext = this.querySelector(
      '[data-nd-gallery-next]'
    );

    this.galleryDesktopCounter = this.querySelector(
      '[data-nd-gallery-desktop-current]'
    );

    this.galleryThumbs = [
      ...this.querySelectorAll(
        '[data-nd-gallery-thumb]'
      )
    ];

    this.galleryItems = [
      ...this.querySelectorAll(
        '[data-nd-gallery-item]'
      )
    ];

    this.galleryTotal = Number(
      this.gallery
        ?.dataset
        .ndGalleryTotal
      || this.galleryItems.length
      || 0
    );

    /* =====================================================
       GALLERY LIGHTBOX
    ===================================================== */

    this.galleryLightbox = this.querySelector(
      '[data-nd-gallery-lightbox]'
    );

    this.galleryLightboxTriggers = [
      ...this.querySelectorAll(
        '[data-nd-gallery-lightbox-trigger]'
      )
    ];

    this.galleryLightboxItems = [
      ...this.querySelectorAll(
        '[data-nd-gallery-lightbox-item]'
      )
    ];

    this.galleryLightboxCloseButtons = [
      ...this.querySelectorAll(
        '[data-nd-gallery-lightbox-close]'
      )
    ];

    this.galleryLightboxPrev = this.querySelector(
      '[data-nd-gallery-lightbox-prev]'
    );

    this.galleryLightboxNext = this.querySelector(
      '[data-nd-gallery-lightbox-next]'
    );

    this.galleryLightboxCounter = this.querySelector(
      '[data-nd-gallery-lightbox-current]'
    );

    this.galleryLightboxLastTrigger =
      null;

    /* =====================================================
       MOBILE GALLERY
    ===================================================== */

    this.mobileGallery = this.querySelector(
      '[data-nd-gallery-mobile]'
    );

    this.mobileTrack = this.querySelector(
      '.nd-gallery__mobile-track'
    );

    this.mobileSlides = [
      ...this.querySelectorAll(
        '[data-nd-gallery-mobile-slide]'
      )
    ];

    this.mobileCounter = this.querySelector(
      '[data-nd-gallery-current]'
    );

    /* =====================================================
       SIZE GUIDE
    ===================================================== */

    this.sizeGuides = [
      ...this.querySelectorAll(
        '[data-nd-size-guide]'
      )
    ];

    this.activeSizeGuide =
      null;

    this.sizeGuideLastTrigger =
      null;
  }

  /* =======================================================
     PARSE MAIN VARIANTS
  ======================================================= */

  parseVariants() {
    this.variants = [];

    if (!this.variantDataElement) {
      return;
    }

    try {
      this.variants =
        JSON.parse(
          this.variantDataElement
            .textContent
        );
    } catch (error) {
      console.error(
        '[Nodara PDP] No fue posible leer las variantes.',
        error
      );
    }
  }

  /* =======================================================
     EVENTS
  ======================================================= */

  bindEvents() {
    this.optionInputs.forEach(
      (input) => {
        input.addEventListener(
          'change',
          () => {
            this.handleOptionChange();
          }
        );
      }
    );

    this.quantityMinus?.addEventListener(
      'click',
      () => {
        this.changeQuantity(-1);
      }
    );

    this.quantityPlus?.addEventListener(
      'click',
      () => {
        this.changeQuantity(1);
      }
    );

    this.quantityInput?.addEventListener(
      'change',
      () => {
        this.normalizeQuantity();
      }
    );

    /* =====================================================
       MAIN ADD TO CART FEEDBACK
    ===================================================== */

    this.productForm?.addEventListener(
      'submit',
      () => {
        this.setAddToCartLoading();
      }
    );

    document.addEventListener(
      'cart:update',
      this.handleCartUpdateBound =
        this.handleCartUpdate.bind(
          this
        )
    );

    document.addEventListener(
      'cart:error',
      this.handleCartErrorBound =
        this.handleCartError.bind(
          this
        )
    );

    this.productFormComponent =
      this.querySelector(
        'product-form-component'
      );

    this.productFormComponent?.addEventListener(
      'product-form:success',
      this.handleProductFormSuccessBound =
        this.handleProductFormSuccess.bind(
          this
        )
    );

    this.productFormComponent?.addEventListener(
      'product-form:error',
      this.handleProductFormErrorBound =
        this.handleProductFormError.bind(
          this
        )
    );

    /* =====================================================
       STICKY ADD TO CART
    ===================================================== */

    this.stickyButton?.addEventListener(
      'click',
      () => {
        this.handleStickyAddToCart();
      }
    );

    /* =====================================================
       GALLERY
    ===================================================== */

    this.galleryThumbs.forEach(
      (thumb) => {
        thumb.addEventListener(
          'click',
          () => {
            this.activateMedia(
              thumb.dataset.mediaId,
              {
                scrollMobile:
                  false
              }
            );
          }
        );
      }
    );

    this.galleryPrev?.addEventListener(
      'click',
      () => {
        this.navigateGallery(
          -1
        );
      }
    );

    this.galleryNext?.addEventListener(
      'click',
      () => {
        this.navigateGallery(
          1
        );
      }
    );

    /* =====================================================
       GALLERY LIGHTBOX
    ===================================================== */

    this.galleryLightboxTriggers.forEach(
      (trigger) => {
        trigger.addEventListener(
          'click',
          () => {
            const mediaId =
              trigger.dataset
                .mediaId;

            this.openGalleryLightbox(
              mediaId,
              trigger
            );
          }
        );
      }
    );

    this.galleryLightboxCloseButtons.forEach(
      (button) => {
        button.addEventListener(
          'click',
          () => {
            this.closeGalleryLightbox();
          }
        );
      }
    );

    this.galleryLightboxPrev?.addEventListener(
      'click',
      () => {
        this.navigateGalleryLightbox(
          -1
        );
      }
    );

    this.galleryLightboxNext?.addEventListener(
      'click',
      () => {
        this.navigateGalleryLightbox(
          1
        );
      }
    );

    this.handleGalleryLightboxKeydownBound =
      this.handleGalleryLightboxKeydown.bind(
        this
      );

    /* =====================================================
       MOBILE GALLERY
    ===================================================== */

    this.mobileTrack?.addEventListener(
      'scroll',
      () => {
        this.handleMobileGalleryScroll();
      },
      {
        passive: true
      }
    );
  }

  /* =======================================================
     INITIAL STATE
  ======================================================= */

  syncInitialState() {
    this.updateSelectedLabels();

    const selectedVariant =
      this.getVariantFromURL()
      || this.getSelectedVariantFromOptions();

    if (!selectedVariant) {
      return;
    }

    this.updateVariant(
      selectedVariant,
      {
        updateUrl:
          false,

        updateGallery:
          false
      }
    );

    this.syncInitialGallery(
      selectedVariant
    );
  }

  /* =======================================================
     MAIN VARIANTS
  ======================================================= */

  handleOptionChange() {
    this.updateSelectedLabels();

    const variant =
      this.getSelectedVariantFromOptions();

    if (!variant) {
      this.setUnavailableCombination();

      return;
    }

    this.updateVariant(
      variant,
      {
        updateUrl:
          true,

        updateGallery:
          true
      }
    );
  }

  getSelectedVariantFromOptions() {
    if (!this.variants.length) {
      return null;
    }

    const selectedOptions =
      this.getSelectedOptions();

    return (
      this.variants.find(
        (variant) => {
          if (
            !Array.isArray(
              variant.options
            )
          ) {
            return false;
          }

          return variant.options.every(
            (
              option,
              index
            ) =>
              option
              === selectedOptions[
                index
              ]
          );
        }
      )
      || null
    );
  }

  getSelectedOptions() {
    const groups = [
      ...this.querySelectorAll(
        '[data-nd-option-group]'
      )
    ];

    return groups
      .sort(
        (
          a,
          b
        ) =>
          Number(
            a.dataset
              .optionPosition
          )
          -
          Number(
            b.dataset
              .optionPosition
          )
      )
      .map(
        (group) =>
          group.querySelector(
            '[data-nd-option-input]:checked'
          )?.value
          ?? null
      );
  }

  getVariantFromURL() {
    const variantId =
      new URLSearchParams(
        window.location.search
      ).get(
        'variant'
      );

    if (!variantId) {
      return null;
    }

    return (
      this.variants.find(
        (variant) =>
          String(
            variant.id
          )
          === String(
            variantId
          )
      )
      || null
    );
  }

  updateSelectedLabels() {
    this.querySelectorAll(
      '[data-nd-option-group]'
    ).forEach(
      (group) => {
        const selected =
          group.querySelector(
            '[data-nd-option-input]:checked'
          );

        const label =
          group.querySelector(
            '[data-selected-option]'
          );

        if (
          selected
          && label
        ) {
          label.textContent =
            selected.value;
        }
      }
    );
  }

  updateVariant(
    variant,
    {
      updateUrl = true,
      updateGallery = true
    } = {}
  ) {
    this.resetAddToCartFeedback();

    this.currentVariant =
      variant;

    this.updateVariantId(
      variant
    );

    this.updatePrice(
      variant
    );

    this.updateInventory(
      variant
    );

    this.updateAddToCart(
      variant
    );

    this.updateSelectedVariantSummary(
      variant
    );

    this.updateSizeGuideSelection();

    this.updateStickyAtc(
      variant
    );

    this.updateSpecifications(
      variant
    );

    if (updateUrl) {
      this.updateUrl(
        variant.id
      );
    }

    if (
      updateGallery
      && variant.featuredMediaId
    ) {
      this.activateMedia(
        String(
          variant
            .featuredMediaId
        ),
        {
          scrollMobile:
            true
        }
      );
    }

    this.dispatchVariantChange(
      variant
    );
  }

  updateVariantId(
    variant
  ) {
    if (
      this.variantIdInput
    ) {
      this.variantIdInput.value =
        variant.id;
    }
  }
    /* =======================================================
     PRICE
  ======================================================= */

  updatePrice(variant) {
    if (this.priceElement) {
      this.priceElement.textContent =
        this.formatMoney(
          variant.price
        );
    }

    const compareAtPrice =
      Number(
        variant.compareAtPrice
        || 0
      );

    const price =
      Number(
        variant.price
        || 0
      );

    const hasDiscount =
      compareAtPrice > price
      && compareAtPrice > 0;

    if (this.comparePriceElement) {
      if (hasDiscount) {
        this.comparePriceElement.textContent =
          this.formatMoney(
            compareAtPrice
          );

        this.comparePriceElement.hidden =
          false;
      } else {
        this.comparePriceElement.hidden =
          true;

        this.comparePriceElement.textContent =
          '';
      }
    }

    this.updateDiscount(
      price,
      compareAtPrice,
      hasDiscount
    );
  }

  updateDiscount(
    price,
    compareAtPrice,
    hasDiscount
  ) {
    const showPercentage =
      this.priceWrapper
        ?.dataset
        .showPercentage
      === 'true';

    const showSaving =
      this.priceWrapper
        ?.dataset
        .showSaving
      === 'true';

    if (!hasDiscount) {
      if (this.discountElement) {
        this.discountElement.hidden =
          true;

        this.discountElement.textContent =
          '';
      }

      if (this.savingElement) {
        this.savingElement.hidden =
          true;

        this.savingElement.textContent =
          '';
      }

      return;
    }

    const saving =
      compareAtPrice - price;

    const percentage =
      Math.round(
        (saving / compareAtPrice)
        * 100
      );

    if (this.discountElement) {
      this.discountElement.hidden =
        !showPercentage;

      if (showPercentage) {
        this.discountElement.textContent =
          `-${percentage}%`;
      }
    }

    if (this.savingElement) {
      this.savingElement.hidden =
        !showSaving;

      if (showSaving) {
        this.savingElement.textContent =
          `Ahorras ${this.formatMoney(
            saving
          )}`;
      }
    }
  }

  /* =======================================================
     INVENTORY
  ======================================================= */

  updateInventory(variant) {
    if (!this.inventoryElement) {
      return;
    }

    const threshold =
      Number(
        this.inventoryElement
          .dataset
          .lowStockThreshold
        || 5
      );

    if (!variant.available) {
      this.inventoryElement.innerHTML =
        '<span class="nd-inventory__soldout">Agotado</span>';

      return;
    }

    const quantity =
      Number(
        variant.inventoryQuantity
      );

    const tracked =
      Boolean(
        variant.inventoryManagement
      );

    if (
      tracked
      && Number.isFinite(
        quantity
      )
      && quantity > 0
      && quantity <= threshold
    ) {
      this.inventoryElement.innerHTML =
        `<span class="nd-inventory__low">Quedan ${quantity} unidades</span>`;

      return;
    }

    this.inventoryElement.innerHTML =
      '<span class="nd-inventory__available">Disponible</span>';
  }

  /* =======================================================
     MAIN ADD TO CART
  ======================================================= */

  updateAddToCart(variant) {
    if (!this.addToCartButton) {
      return;
    }

    this.addToCartButton.disabled =
      !variant.available;

    if (this.addToCartText) {
      this.addToCartText.textContent =
        variant.available
          ? this.getDefaultAddToCartText()
          : 'Agotado';
    }
  }

  /* =======================================================
     MAIN ADD TO CART FEEDBACK
  ======================================================= */

  setAddToCartLoading() {
    if (
      !this.addToCartButton
      || !this.addToCartText
    ) {
      return;
    }

    if (
      this.addToCartResetTimer
    ) {
      clearTimeout(
        this.addToCartResetTimer
      );

      this.addToCartResetTimer =
        null;
    }

    const loadingText =
      this.addToCartButton
        .dataset
        .ndAtcLoadingText
      || 'Agregando…';

    this.addToCartButton
      .classList
      .remove(
        'is-success',
        'is-error'
      );

    this.addToCartButton
      .classList
      .add(
        'is-loading'
      );

    this.addToCartButton.disabled =
      true;

    this.addToCartButton.setAttribute(
      'aria-busy',
      'true'
    );

    this.addToCartText.textContent =
      loadingText;

    if (
      this.addToCartSpinner
    ) {
      this.addToCartSpinner.hidden =
        false;
    }

    if (
      this.addToCartStatus
    ) {
      this.addToCartStatus.textContent =
        'Agregando producto al carro.';
    }
  }

  setAddToCartSuccess() {
    if (
      !this.addToCartButton
      || !this.addToCartText
    ) {
      return;
    }

    const successText =
      this.addToCartButton
        .dataset
        .ndAtcSuccessText
      || 'Agregado ✓';

    this.addToCartButton
      .classList
      .remove(
        'is-loading',
        'is-error'
      );

    this.addToCartButton
      .classList
      .add(
        'is-success'
      );

    this.addToCartButton
      .removeAttribute(
        'aria-busy'
      );

    if (
      this.addToCartSpinner
    ) {
      this.addToCartSpinner.hidden =
        true;
    }

    this.addToCartText.textContent =
      successText;

    if (
      this.addToCartStatus
    ) {
      this.addToCartStatus.textContent =
        'Producto agregado correctamente al carro.';
    }

    if (
      this.addToCartResetTimer
    ) {
      clearTimeout(
        this.addToCartResetTimer
      );
    }

    this.addToCartResetTimer =
      window.setTimeout(
        () => {
          this.resetAddToCartFeedback();
        },
        1500
      );
  }

  setAddToCartError() {
    if (
      !this.addToCartButton
      || !this.addToCartText
    ) {
      return;
    }

    this.addToCartButton
      .classList
      .remove(
        'is-loading',
        'is-success'
      );

    this.addToCartButton
      .classList
      .add(
        'is-error'
      );

    this.addToCartButton
      .removeAttribute(
        'aria-busy'
      );

    if (
      this.addToCartSpinner
    ) {
      this.addToCartSpinner.hidden =
        true;
    }

    this.addToCartText.textContent =
      'Reintentar';

    if (
      this.addToCartStatus
    ) {
      this.addToCartStatus.textContent =
        'No fue posible agregar el producto. Intenta nuevamente.';
    }

    const variant =
      this.getSelectedVariantFromOptions();

    this.addToCartButton.disabled =
      !variant
      || !variant.available;
  }

  resetAddToCartFeedback() {
    if (
      !this.addToCartButton
      || !this.addToCartText
    ) {
      return;
    }

    if (
      this.addToCartResetTimer
    ) {
      clearTimeout(
        this.addToCartResetTimer
      );

      this.addToCartResetTimer =
        null;
    }

    this.addToCartButton
      .classList
      .remove(
        'is-loading',
        'is-success',
        'is-error'
      );

    this.addToCartButton
      .removeAttribute(
        'aria-busy'
      );

    if (
      this.addToCartSpinner
    ) {
      this.addToCartSpinner.hidden =
        true;
    }

    if (
      this.addToCartStatus
    ) {
      this.addToCartStatus.textContent =
        '';
    }

    const variant =
      this.getSelectedVariantFromOptions();

    if (!variant) {
      this.addToCartButton.disabled =
        true;

      this.addToCartText.textContent =
        'Combinación no disponible';

      return;
    }

    if (!variant.available) {
      this.addToCartButton.disabled =
        true;

      this.addToCartText.textContent =
        'Agotado';

      return;
    }

    this.addToCartButton.disabled =
      false;

    this.addToCartText.textContent =
      this.addToCartButton
        .dataset
        .ndAtcDefaultText
      || 'Agregar al carro';
  }

  /* =======================================================
     HORIZON CART EVENTS
  ======================================================= */

  handleCartUpdate() {
    if (
      this.addToCartButton
        ?.classList
        .contains(
          'is-loading'
        )
    ) {
      this.setAddToCartSuccess();
    }
  }

  handleCartError() {
    if (
      this.addToCartButton
        ?.classList
        .contains(
          'is-loading'
        )
    ) {
      this.setAddToCartError();
    }
  }

  handleProductFormSuccess() {
    if (
      this.addToCartButton
        ?.classList
        .contains(
          'is-loading'
        )
    ) {
      this.setAddToCartSuccess();
    }
  }

  handleProductFormError() {
    if (
      this.addToCartButton
        ?.classList
        .contains(
          'is-loading'
        )
    ) {
      this.setAddToCartError();
    }
  }

  /* =======================================================
     SELECTED VARIANT SUMMARY
  ======================================================= */

  updateSelectedVariantSummary(
    variant
  ) {
    if (
      !this.selectedVariantValue
      || !variant
    ) {
      return;
    }

    const options =
      Array.isArray(
        variant.options
      )
        ? variant.options
        : [];

    const values =
      options
        .filter(
          (value) =>
            value != null
            && String(
              value
            ).trim() !== ''
        )
        .map(
          (value) =>
            String(
              value
            ).trim()
        );

    this.selectedVariantValue.textContent =
      values.length
        ? values.join(
            ' · '
          )
        : '—';
  }

  getDefaultAddToCartText() {
    return (
      this.addToCartButton
        ?.dataset
        .ndAtcDefaultText
      || 'Agregar al carro'
    );
  }

  setUnavailableCombination() {
    this.currentVariant =
      null;

    if (
      this.variantIdInput
    ) {
      this.variantIdInput.value =
        '';
    }

    if (
      this.addToCartButton
    ) {
      this.addToCartButton.disabled =
        true;
    }

    if (
      this.addToCartText
    ) {
      this.addToCartText.textContent =
        'Combinación no disponible';
    }

    if (
      this.stickyButton
    ) {
      this.stickyButton.disabled =
        true;
    }

    if (
      this.stickyButtonText
    ) {
      this.stickyButtonText.textContent =
        'No disponible';
    }
  }

  /* =======================================================
     URL
  ======================================================= */

  updateUrl(variantId) {
    const url =
      new URL(
        window.location.href
      );

    url.searchParams.set(
      'variant',
      variantId
    );

    window.history.replaceState(
      {
        variantId
      },
      '',
      url.toString()
    );
  }

  /* =======================================================
     QUANTITY
  ======================================================= */

  changeQuantity(delta) {
    if (!this.quantityInput) {
      return;
    }

    const current =
      Number(
        this.quantityInput.value
      )
      || 1;

    const minimum =
      Number(
        this.quantityInput.min
      )
      || 1;

    this.quantityInput.value =
      Math.max(
        minimum,
        current + delta
      );

    this.quantityInput.dispatchEvent(
      new Event(
        'change',
        {
          bubbles: true
        }
      )
    );
  }

  normalizeQuantity() {
    if (!this.quantityInput) {
      return;
    }

    const minimum =
      Number(
        this.quantityInput.min
      )
      || 1;

    let value =
      Number(
        this.quantityInput.value
      );

    if (
      !Number.isFinite(
        value
      )
      || value < minimum
    ) {
      value =
        minimum;
    }

    this.quantityInput.value =
      Math.floor(
        value
      );
  }

  /* =======================================================
     SPECIFICATIONS
  ======================================================= */

  updateSpecifications(variant) {
    this.specificationSkuValues.forEach(
      (element) => {
        element.textContent =
          variant.sku
          || '—';
      }
    );
  }

  /* =======================================================
     DELIVERY
  ======================================================= */

  updateDeliveryDate() {
    if (
      !this.deliveryElement
      || !this.deliveryDateElement
    ) {
      return;
    }

    const minDays =
      Number(
        this.deliveryElement
          .dataset
          .deliveryDaysMin
        || 2
      );

    const maxDays =
      Number(
        this.deliveryElement
          .dataset
          .deliveryDaysMax
        || 4
      );

    const minDate =
      this.addBusinessDays(
        new Date(),
        minDays
      );

    const maxDate =
      this.addBusinessDays(
        new Date(),
        maxDays
      );

    const formatter =
      new Intl.DateTimeFormat(
        'es-CL',
        {
          weekday:
            'long',

          day:
            'numeric',

          month:
            'short'
        }
      );

    this.deliveryDateElement.textContent =
      `Recíbelo entre ${formatter.format(
        minDate
      )} y ${formatter.format(
        maxDate
      )}`;
  }

  addBusinessDays(
    date,
    days
  ) {
    const result =
      new Date(
        date
      );

    let added =
      0;

    while (
      added < days
    ) {
      result.setDate(
        result.getDate()
        + 1
      );

      const day =
        result.getDay();

      if (
        day !== 0
        && day !== 6
      ) {
        added += 1;
      }
    }

    return result;
  }

  /* =======================================================
     STICKY
  ======================================================= */

  initializeStickyAtc() {
    if (
      !this.stickyAtc
      || !this.addToCartButton
      || !(
        'IntersectionObserver'
        in window
      )
    ) {
      return;
    }

    const enabled =
      this.stickyAtc
        .dataset
        .ndStickyEnabled
      === 'true';

    if (!enabled) {
      return;
    }

    /* =====================================================
       RESET OBSERVERS
    ===================================================== */

    if (
      this.stickyObserver
    ) {
      this.stickyObserver.disconnect();

      this.stickyObserver =
        null;
    }

    if (
      this.stickyFooterObserver
    ) {
      this.stickyFooterObserver.disconnect();

      this.stickyFooterObserver =
        null;
    }

    this.stickyMainCtaPassed =
      false;

    this.stickyFooterVisible =
      false;

    /* =====================================================
       MAIN CTA OBSERVER
    ===================================================== */

    this.stickyObserver =
      new IntersectionObserver(
        ([entry]) => {
          if (!entry) {
            return;
          }

          this.stickyMainCtaPassed =
            !entry.isIntersecting
            && entry
              .boundingClientRect
              .top
            < 0;

          this.updateStickyVisibility();
        },
        {
          threshold: 0
        }
      );

    this.stickyObserver.observe(
      this.addToCartButton
    );

    /* =====================================================
       FOOTER OBSERVER
    ===================================================== */

    if (
      this.stickyFooter
    ) {
      this.stickyFooterObserver =
        new IntersectionObserver(
          ([entry]) => {
            if (!entry) {
              return;
            }

            this.stickyFooterVisible =
              entry.isIntersecting;

            this.updateStickyVisibility();
          },
          {
            threshold: 0,

            /*
             * Ocultamos el sticky apenas
             * el footer comienza a entrar.
             */
            rootMargin:
              '0px 0px 0px 0px'
          }
        );

      this.stickyFooterObserver.observe(
        this.stickyFooter
      );
    }

    /*
     * Aplica el estado inicial.
     */
    this.updateStickyVisibility();
  }

  updateStickyVisibility() {
    if (!this.stickyAtc) {
      return;
    }

    const show =
      this.stickyMainCtaPassed
      && !this.stickyFooterVisible;

    this.stickyAtc
      .classList
      .toggle(
        'is-visible',
        show
      );

    this.stickyAtc.setAttribute(
      'aria-hidden',
      show
        ? 'false'
        : 'true'
    );
  }

  updateStickyAtc(variant) {
    if (!this.stickyAtc) {
      return;
    }

    if (
      this.stickyPrice
    ) {
      this.stickyPrice.textContent =
        this.formatMoney(
          variant.price
        );
    }

    if (
      this.stickyVariant
    ) {
      this.stickyVariant.textContent =
        this.getSelectedOptions()
          .filter(
            Boolean
          )
          .join(
            ' · '
          );
    }

    if (
      this.stickyButton
    ) {
      this.stickyButton.disabled =
        !variant.available;
    }

    if (
      this.stickyButtonText
    ) {
      this.stickyButtonText.textContent =
        variant.available
          ? 'Agregar'
          : 'Agotado';
    }
  }

  handleStickyAddToCart() {
    if (
      this.currentVariant
        ?.available
      && this.addToCartButton
    ) {
      this.addToCartButton.click();
    }
  }
    /* =======================================================
     DETAILS / TABS
  ======================================================= */

  initializeProductDetails() {
    this.productDetails.forEach(
      (details) => {
        this.setupProductDetails(
          details
        );
      }
    );
  }

  setupProductDetails(details) {
    if (
      details.dataset.desktopStyle
      !== 'tabs'
    ) {
      return;
    }

    const tabsContainer =
      details.querySelector(
        '[data-nd-details-tabs]'
      );

    const content =
      details.querySelector(
        '.nd-product-details__content'
      );

    if (
      !tabsContainer
      || !content
    ) {
      return;
    }

    const accordions = [
      ...content.querySelectorAll(
        '[data-nd-accordion]'
      )
    ];

    if (!accordions.length) {
      return;
    }

    tabsContainer.innerHTML =
      '';

    let initialIndex =
      accordions.findIndex(
        (accordion) =>
          accordion.hasAttribute(
            'open'
          )
      );

    if (initialIndex < 0) {
      initialIndex = 0;
    }

    accordions.forEach(
      (
        accordion,
        index
      ) => {
        const accordionId =
          accordion.dataset
            .ndAccordionId
          || `nd-details-${index}`;

        accordion.dataset
          .ndAccordionId =
          accordionId;

        const button =
          document.createElement(
            'button'
          );

        button.type =
          'button';

        button.className =
          'nd-details-tab';

        button.textContent =
          accordion.dataset
            .ndAccordionTitle
          || `Sección ${index + 1}`;

        button.dataset
          .ndDetailsTab =
          accordionId;

        button.setAttribute(
          'role',
          'tab'
        );

        button.addEventListener(
          'click',
          () => {
            this.activateDetailsTab(
              details,
              accordionId
            );
          }
        );

        tabsContainer.appendChild(
          button
        );
      }
    );

    this.activateDetailsTab(
      details,
      accordions[
        initialIndex
      ].dataset
        .ndAccordionId
    );
  }

  activateDetailsTab(
    details,
    accordionId
  ) {
    const accordions = [
      ...details.querySelectorAll(
        '[data-nd-accordion]'
      )
    ];

    const tabs = [
      ...details.querySelectorAll(
        '[data-nd-details-tab]'
      )
    ];

    accordions.forEach(
      (accordion) => {
        const active =
          accordion.dataset
            .ndAccordionId
          === accordionId;

        accordion.classList.toggle(
          'is-tab-active',
          active
        );

        if (active) {
          accordion.setAttribute(
            'open',
            ''
          );
        } else {
          accordion.removeAttribute(
            'open'
          );
        }
      }
    );

    tabs.forEach(
      (tab) => {
        const active =
          tab.dataset
            .ndDetailsTab
          === accordionId;

        tab.classList.toggle(
          'is-active',
          active
        );

        tab.setAttribute(
          'aria-selected',
          active
            ? 'true'
            : 'false'
        );
      }
    );
  }

  /* =======================================================
     CROSS SELL INITIALIZATION
  ======================================================= */

  initializeCrossSell() {
    this.crossSellBlocks.forEach(
      async (block) => {
        const source =
          block.dataset
            .ndCrossSellSource;

        if (
          source
          === 'recommendations'
        ) {
          await this.loadCrossSellRecommendations(
            block
          );
        }

        this.setupCrossSellCarousel(
          block
        );

        this.initializeCrossSellVariants(
          block
        );

        this.initializeCrossSellForms(
          block
        );
      }
    );
  }

  /* =======================================================
     CROSS SELL CAROUSEL
  ======================================================= */

  setupCrossSellCarousel(
    block
  ) {
    if (!block) {
      return;
    }

    const track =
      block.querySelector(
        '[data-nd-cross-sell-track]'
      );

    const prev =
      block.querySelector(
        '[data-nd-cross-sell-prev]'
      );

    const next =
      block.querySelector(
        '[data-nd-cross-sell-next]'
      );

    const controls =
      block.querySelector(
        '[data-nd-cross-sell-controls]'
      );

    if (!track) {
      return;
    }

    const cards = [
      ...track.querySelectorAll(
        '[data-nd-cross-sell-card]'
      )
    ];

    if (!cards.length) {
      if (controls) {
        controls.hidden =
          true;
      }

      return;
    }

    const getVisibleCount =
      () => {
        if (
          window.matchMedia(
            '(max-width: 749px)'
          ).matches
        ) {
          return 1;
        }

        if (
          window.matchMedia(
            '(max-width: 989px)'
          ).matches
        ) {
          return 2;
        }

        return 3;
      };

    const getPageCount =
      () =>
        Math.ceil(
          cards.length
          / getVisibleCount()
        );

    const getCurrentPage =
      () => {
        const visible =
          getVisibleCount();

        const closest =
          cards.reduce(
            (
              current,
              card,
              index
            ) => {
              const distance =
                Math.abs(
                  card.offsetLeft
                  - track.offsetLeft
                  - track.scrollLeft
                );

              if (
                distance
                < current.distance
              ) {
                return {
                  index,
                  distance
                };
              }

              return current;
            },
            {
              index: 0,
              distance: Infinity
            }
          );

        return Math.floor(
          closest.index
          / visible
        );
      };

    const scrollToPage =
      (
        pageIndex,
        behavior = 'smooth'
      ) => {
        const visible =
          getVisibleCount();

        const pageCount =
          getPageCount();

        const safePage =
          Math.max(
            0,
            Math.min(
              pageIndex,
              pageCount - 1
            )
          );

        const targetCard =
          cards[
            safePage * visible
          ];

        if (!targetCard) {
          return;
        }

        track.scrollTo({
          left:
            targetCard.offsetLeft
            - track.offsetLeft,

          behavior
        });
      };

    const rebuildPagination =
      () => {
        this.buildCrossSellPagination(
          block,
          getPageCount(),
          scrollToPage
        );
      };

    const updateState =
      () => {
        const currentPage =
          getCurrentPage();

        const pageCount =
          getPageCount();

        if (prev) {
          prev.disabled =
            currentPage <= 0;
        }

        if (next) {
          next.disabled =
            currentPage
            >= pageCount - 1;
        }

        if (controls) {
          controls.hidden =
            pageCount <= 1;
        }

        this.updateCrossSellPagination(
          block,
          currentPage
        );
      };

    prev?.addEventListener(
      'click',
      () => {
        scrollToPage(
          getCurrentPage() - 1
        );
      }
    );

    next?.addEventListener(
      'click',
      () => {
        scrollToPage(
          getCurrentPage() + 1
        );
      }
    );

    let scrollFrame =
      null;

    track.addEventListener(
      'scroll',
      () => {
        if (scrollFrame) {
          cancelAnimationFrame(
            scrollFrame
          );
        }

        scrollFrame =
          requestAnimationFrame(
            updateState
          );
      },
      {
        passive: true
      }
    );

    let resizeTimer =
      null;

    window.addEventListener(
      'resize',
      () => {
        clearTimeout(
          resizeTimer
        );

        resizeTimer =
          setTimeout(
            () => {
              rebuildPagination();

              scrollToPage(
                Math.min(
                  getCurrentPage(),
                  getPageCount() - 1
                ),
                'auto'
              );

              updateState();
            },
            120
          );
      }
    );

    rebuildPagination();
    updateState();
  }

  buildCrossSellPagination(
    block,
    pageCount,
    scrollToPage
  ) {
    const pagination =
      block.querySelector(
        '[data-nd-cross-sell-pagination]'
      );

    if (!pagination) {
      return;
    }

    pagination.innerHTML =
      '';

    if (
      pageCount <= 1
    ) {
      return;
    }

    for (
      let index = 0;
      index < pageCount;
      index += 1
    ) {
      const dot =
        document.createElement(
          'button'
        );

      dot.type =
        'button';

      dot.className =
        'nd-cross-sell__dot';

      dot.setAttribute(
        'aria-label',
        `Ir a la página ${index + 1}`
      );

      dot.dataset
        .ndCrossSellPage =
        String(
          index
        );

      dot.addEventListener(
        'click',
        () => {
          scrollToPage(
            index
          );
        }
      );

      pagination.appendChild(
        dot
      );
    }
  }

  updateCrossSellPagination(
    block,
    currentPage
  ) {
    const dots = [
      ...block.querySelectorAll(
        '.nd-cross-sell__dot'
      )
    ];

    dots.forEach(
      (
        dot,
        index
      ) => {
        const active =
          index
          === currentPage;

        dot.classList.toggle(
          'is-active',
          active
        );

        dot.setAttribute(
          'aria-current',
          active
            ? 'true'
            : 'false'
        );
      }
    );
  }

  /* =======================================================
     CROSS SELL QUICK VARIANTS
  ======================================================= */

  initializeCrossSellVariants(
    block
  ) {
    if (!block) {
      return;
    }

    const cards = [
      ...block.querySelectorAll(
        '[data-nd-cross-sell-card]'
      )
    ];

    cards.forEach(
      (card) => {
        this.setupCrossSellVariantCard(
          card
        );
      }
    );
  }

  setupCrossSellVariantCard(
    card
  ) {
    const picker =
      card.querySelector(
        '[data-nd-cross-variant-picker]'
      );

    const dataElement =
      card.querySelector(
        '[data-nd-cross-variants]'
      );

    if (
      !picker
      || !dataElement
    ) {
      return;
    }

    let variants =
      [];

    try {
      variants =
        JSON.parse(
          dataElement.textContent
        );
    } catch (error) {
      console.error(
        '[Nodara PDP] Error leyendo variantes del cross-sell.',
        error
      );

      return;
    }

    if (
      !Array.isArray(
        variants
      )
      || !variants.length
    ) {
      return;
    }

    const optionInputs = [
      ...picker.querySelectorAll(
        '[data-nd-cross-option-input]'
      )
    ];

    const update = (
      changedInput = null
    ) => {
      let selectedOptions =
        this.getCrossSellSelectedOptions(
          picker
        );

      this.updateCrossSellOptionAvailability(
        picker,
        variants,
        selectedOptions
      );

      let variant =
        this.findCrossSellVariant(
          variants,
          selectedOptions
        );

      if (
        !variant
        || !variant.available
      ) {
        const fallbackVariant =
          this.findCrossSellFallbackVariant(
            variants,
            selectedOptions,
            changedInput
          );

        if (fallbackVariant) {
          this.applyCrossSellVariantSelection(
            picker,
            fallbackVariant
          );

          selectedOptions =
            this.getCrossSellSelectedOptions(
              picker
            );

          variant =
            fallbackVariant;

          this.updateCrossSellOptionAvailability(
            picker,
            variants,
            selectedOptions
          );
        }
      }

      this.updateCrossSellVariantCard(
        card,
        variant
      );
    };

    optionInputs.forEach(
      (input) => {
        input.addEventListener(
          'change',
          () => {
            update(
              input
            );
          }
        );
      }
    );

    update();
  }

  getCrossSellSelectedOptions(
    picker
  ) {
    const groups = [
      ...picker.querySelectorAll(
        '[data-nd-cross-option]'
      )
    ];

    return groups
      .sort(
        (
          a,
          b
        ) =>
          Number(
            a.dataset
              .optionPosition
          )
          -
          Number(
            b.dataset
              .optionPosition
          )
      )
      .map(
        (group) =>
          group.querySelector(
            '[data-nd-cross-option-input]:checked'
          )?.value
          ?? null
      );
  }

  findCrossSellVariant(
    variants,
    selectedOptions
  ) {
    return (
      variants.find(
        (variant) => {
          if (
            !Array.isArray(
              variant.options
            )
          ) {
            return false;
          }

          return variant.options.every(
            (
              option,
              index
            ) =>
              option
              === selectedOptions[
                index
              ]
          );
        }
      )
      || null
    );
  }

  findCrossSellFallbackVariant(
    variants,
    selectedOptions,
    changedInput = null
  ) {
    const availableVariants =
      variants.filter(
        (variant) =>
          variant.available
          && Array.isArray(
            variant.options
          )
      );

    if (
      !availableVariants.length
    ) {
      return null;
    }

    if (changedInput) {
      const changedPosition =
        Number(
          changedInput.dataset
            .optionPosition
        ) - 1;

      const changedValue =
        changedInput.value;

      const preservingChanged =
        availableVariants.find(
          (variant) =>
            variant.options[
              changedPosition
            ]
            === changedValue
        );

      if (
        preservingChanged
      ) {
        return preservingChanged;
      }
    }

    let bestVariant =
      availableVariants[0];

    let bestScore =
      -1;

    availableVariants.forEach(
      (variant) => {
        let score =
          0;

        variant.options.forEach(
          (
            option,
            index
          ) => {
            if (
              option
              === selectedOptions[
                index
              ]
            ) {
              score += 1;
            }
          }
        );

        if (
          score > bestScore
        ) {
          bestScore =
            score;

          bestVariant =
            variant;
        }
      }
    );

    return bestVariant;
  }
    applyCrossSellVariantSelection(
    picker,
    variant
  ) {
    if (
      !picker
      || !variant
      || !Array.isArray(
        variant.options
      )
    ) {
      return;
    }

    variant.options.forEach(
      (
        optionValue,
        index
      ) => {
        const position =
          index + 1;

        const inputs = [
          ...picker.querySelectorAll(
            `[data-nd-cross-option-input][data-option-position="${position}"]`
          )
        ];

        inputs.forEach(
          (input) => {
            input.checked =
              input.value
              === optionValue;
          }
        );
      }
    );
  }

  updateCrossSellOptionAvailability(
    picker,
    variants,
    selectedOptions
  ) {
    const groups = [
      ...picker.querySelectorAll(
        '[data-nd-cross-option]'
      )
    ];

    groups.forEach(
      (group) => {
        const position =
          Number(
            group.dataset
              .optionPosition
          ) - 1;

        const inputs = [
          ...group.querySelectorAll(
            '[data-nd-cross-option-input]'
          )
        ];

        inputs.forEach(
          (input) => {
            const candidateValue =
              input.value;

            const validVariantExists =
              variants.some(
                (variant) => {
                  if (
                    !variant.available
                    || !Array.isArray(
                      variant.options
                    )
                  ) {
                    return false;
                  }

                  return variant.options.every(
                    (
                      variantOption,
                      index
                    ) => {
                      if (
                        index
                        === position
                      ) {
                        return (
                          variantOption
                          === candidateValue
                        );
                      }

                      const selected =
                        selectedOptions[
                          index
                        ];

                      if (
                        selected
                        == null
                      ) {
                        return true;
                      }

                      return (
                        variantOption
                        === selected
                      );
                    }
                  );
                }
              );

            input.disabled =
              !validVariantExists;

            let label =
              null;

            if (
              input.id
              && typeof CSS
                !== 'undefined'
              && CSS.escape
            ) {
              label =
                picker.querySelector(
                  `label[for="${CSS.escape(
                    input.id
                  )}"]`
                );
            } else if (
              input.id
            ) {
              label =
                picker.querySelector(
                  `label[for="${input.id}"]`
                );
            }

            if (label) {
              label.classList.toggle(
                'is-unavailable',
                !validVariantExists
              );

              label.setAttribute(
                'aria-disabled',
                validVariantExists
                  ? 'false'
                  : 'true'
              );
            }
          }
        );
      }
    );
  }

  updateCrossSellVariantCard(
    card,
    variant
  ) {
    const variantInput =
      card.querySelector(
        '[data-nd-cross-variant-id]'
      );

    const price =
      card.querySelector(
        '[data-nd-cross-price]'
      );

    const compare =
      card.querySelector(
        '[data-nd-cross-compare]'
      );

    const image =
      card.querySelector(
        '[data-nd-cross-image]'
      );

    const button =
      card.querySelector(
        '[data-nd-cross-sell-add]'
      );

    const buttonText =
      card.querySelector(
        '[data-nd-cross-sell-add-text]'
      );

    if (!variant) {
      if (variantInput) {
        variantInput.value =
          '';
      }

      if (button) {
        button.disabled =
          true;
      }

      if (buttonText) {
        buttonText.textContent =
          'No disponible';
      }

      return;
    }

    /* =====================================================
       VARIANT ID
    ===================================================== */

    if (variantInput) {
      variantInput.value =
        variant.id;
    }

    /* =====================================================
       PRICE
    ===================================================== */

    if (price) {
      price.textContent =
        this.formatMoney(
          variant.price
        );
    }

    const compareAtPrice =
      Number(
        variant.compareAtPrice
        || 0
      );

    const currentPrice =
      Number(
        variant.price
        || 0
      );

    if (compare) {
      if (
        compareAtPrice
        > currentPrice
      ) {
        compare.hidden =
          false;

        compare.textContent =
          this.formatMoney(
            compareAtPrice
          );
      } else {
        compare.hidden =
          true;

        compare.textContent =
          '';
      }
    }

    /* =====================================================
       IMAGE
    ===================================================== */

    if (
      image
      && variant.image
    ) {
      const currentSource =
        image.getAttribute(
          'src'
        );

      if (
        currentSource
        !== variant.image
      ) {
        image.classList.add(
          'is-changing'
        );

        const preload =
          new Image();

        preload.onload =
          () => {
            image.src =
              variant.image;

            image.removeAttribute(
              'srcset'
            );

            requestAnimationFrame(
              () => {
                image.classList.remove(
                  'is-changing'
                );
              }
            );
          };

        preload.onerror =
          () => {
            image.classList.remove(
              'is-changing'
            );
          };

        preload.src =
          variant.image;
      }
    }

    /* =====================================================
       AVAILABILITY
    ===================================================== */

    if (button) {
      button.disabled =
        !variant.available;
    }

    if (buttonText) {
      const parentBlock =
        card.closest(
          '[data-nd-cross-sell]'
        );

      const defaultText =
        parentBlock
          ?.dataset
          .ndCrossSellButtonText
        || 'Agregar';

      buttonText.textContent =
        variant.available
          ? defaultText
          : 'Agotado';
    }
  }

  /* =======================================================
     CROSS SELL FORMS
  ======================================================= */

  initializeCrossSellForms(
    block
  ) {
    if (!block) {
      return;
    }

    const forms = [
      ...block.querySelectorAll(
        '[data-nd-cross-sell-form]'
      )
    ];

    forms.forEach(
      (form) => {
        const horizonComponent =
          form.closest(
            'product-form-component'
          );

        if (
          horizonComponent
          && customElements.get(
            'product-form-component'
          )
        ) {
          return;
        }

        form.addEventListener(
          'submit',
          (event) => {
            event.preventDefault();

            this.handleCrossSellAjaxAdd(
              form
            );
          }
        );
      }
    );
  }

  async handleCrossSellAjaxAdd(
    form
  ) {
    if (!form) {
      return;
    }

    const button =
      form.querySelector(
        '[data-nd-cross-sell-add]'
      );

    const text =
      form.querySelector(
        '[data-nd-cross-sell-add-text]'
      );

    if (
      !button
      || button.disabled
    ) {
      return;
    }

    const originalText =
      text?.textContent
      || 'Agregar';

    button.disabled =
      true;

    if (text) {
      text.textContent =
        'Agregando…';
    }

    const root =
      window.Shopify
        ?.routes
        ?.root
      || '/';

    try {
      const response =
        await fetch(
          `${root}cart/add.js`,
          {
            method:
              'POST',

            headers: {
              Accept:
                'application/json'
            },

            body:
              new FormData(
                form
              )
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.description
          || data.message
          || 'No fue posible agregar.'
        );
      }

      if (text) {
        text.textContent =
          'Agregado ✓';
      }

      await this.dispatchCartUpdate(
        data
      );

      setTimeout(
        () => {
          button.disabled =
            false;

          if (text) {
            text.textContent =
              originalText;
          }
        },
        1200
      );
    } catch (error) {
      console.error(
        '[Nodara PDP] Error agregando producto.',
        error
      );

      if (text) {
        text.textContent =
          'Error';
      }

      setTimeout(
        () => {
          button.disabled =
            false;

          if (text) {
            text.textContent =
              originalText;
          }
        },
        1400
      );
    }
  }

  async dispatchCartUpdate(
    addedItem
  ) {
    const root =
      window.Shopify
        ?.routes
        ?.root
      || '/';

    let cart =
      null;

    try {
      const response =
        await fetch(
          `${root}cart.js`,
          {
            headers: {
              Accept:
                'application/json'
            }
          }
        );

      if (response.ok) {
        cart =
          await response.json();
      }
    } catch (error) {
      console.warn(
        '[Nodara PDP] No fue posible leer carrito.',
        error
      );
    }

    document.dispatchEvent(
      new CustomEvent(
        'cart:update',
        {
          bubbles:
            true,

          detail: {
            data: {
              itemCount:
                cart?.item_count
                ?? 1,

              source:
                'nodara-cross-sell',

              cart,
              addedItem
            }
          }
        }
      )
    );

    document.dispatchEvent(
      new CustomEvent(
        'nodara:cart-update',
        {
          bubbles:
            true,

          detail: {
            cart,
            addedItem
          }
        }
      )
    );
  }

  /* =======================================================
     SHOPIFY RECOMMENDATIONS
  ======================================================= */

  async loadCrossSellRecommendations(
    block
  ) {
    const productId =
      block.dataset
        .ndCrossSellProductId;

    const limit =
      Number(
        block.dataset
          .ndCrossSellLimit
        || 6
      );

    const track =
      block.querySelector(
        '[data-nd-cross-sell-track]'
      );

    if (
      !productId
      || !track
    ) {
      return;
    }

    const root =
      window.Shopify
        ?.routes
        ?.root
      || '/';

    try {
      const response =
        await fetch(
          `${root}recommendations/products.json?product_id=${encodeURIComponent(
            productId
          )}&limit=${encodeURIComponent(
            limit
          )}&intent=related`,
          {
            headers: {
              Accept:
                'application/json'
            }
          }
        );

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}`
        );
      }

      const data =
        await response.json();

      const products =
        Array.isArray(
          data.products
        )
          ? data.products
          : [];

      track.innerHTML =
        '';

      products
        .filter(
          (
            recommendedProduct
          ) =>
            String(
              recommendedProduct.id
            )
            !== String(
              productId
            )
        )
        .slice(
          0,
          limit
        )
        .forEach(
          (
            recommendedProduct
          ) => {
            const card =
              this.createRecommendationCard(
                recommendedProduct,
                block
              );

            if (card) {
              track.appendChild(
                card
              );
            }
          }
        );

      if (
        !track.children.length
      ) {
        track.innerHTML =
          '<div class="nd-cross-sell__empty">No hay recomendaciones disponibles.</div>';
      }
    } catch (error) {
      console.error(
        '[Nodara PDP] Error cargando recomendaciones.',
        error
      );

      track.innerHTML =
        '<div class="nd-cross-sell__empty">No fue posible cargar recomendaciones.</div>';
    }
  }

  createRecommendationCard(
    product,
    block
  ) {
    if (!product) {
      return null;
    }

    const variants =
      Array.isArray(
        product.variants
      )
        ? product.variants
        : [];

    const variant =
      variants.find(
        (item) =>
          item.available
      )
      || variants[0];

    if (!variant) {
      return null;
    }

    const article =
      document.createElement(
        'article'
      );

    article.className =
      'nd-cross-sell-card';

    article.dataset
      .ndCrossSellCard =
      '';

    let imageUrl =
      '';

    if (
      typeof product
        .featured_image
      === 'string'
    ) {
      imageUrl =
        product
          .featured_image;
    } else if (
      product
        .featured_image
    ) {
      imageUrl =
        product
          .featured_image
          .url
        || product
          .featured_image
          .src
        || '';
    }

    const root =
      window.Shopify
        ?.routes
        ?.root
      || '/';

    const buttonText =
      block.dataset
        .ndCrossSellButtonText
      || 'Agregar';

    const compareAtPrice =
      Number(
        variant
          .compare_at_price
        || 0
      );

    const price =
      Number(
        variant.price
        || 0
      );

    const compareMarkup =
      compareAtPrice > price
        ? `
          <span
            class="nd-cross-sell-card__price-compare"
          >
            ${this.formatMoney(
              compareAtPrice
            )}
          </span>
        `
        : '';

    article.innerHTML = `
      <a
        class="nd-cross-sell-card__media"
        href="${product.url || '#'}"
      >
        ${
          imageUrl
            ? `
              <img
                class="nd-cross-sell-card__image"
                src="${imageUrl}"
                alt=""
                loading="lazy"
                data-nd-cross-image
              >
            `
            : `
              <div
                class="nd-cross-sell-card__placeholder"
              >
                Sin imagen
              </div>
            `
        }
      </a>

      <div
        class="nd-cross-sell-card__body"
      >

        <a
          href="${product.url || '#'}"
          class="nd-cross-sell-card__title"
        >
          ${this.escapeHtml(
            product.title
            || ''
          )}
        </a>

        <div
          class="nd-cross-sell-card__price"
        >

          <span
            class="nd-cross-sell-card__price-current"
            data-nd-cross-price
          >
            ${this.formatMoney(
              price
            )}
          </span>

          ${compareMarkup}

        </div>

        <form
          method="post"
          action="${root}cart/add"
          class="nd-cross-sell-card__form"
          data-nd-cross-sell-form
        >

          <input
            type="hidden"
            name="id"
            value="${variant.id}"
            data-nd-cross-variant-id
          >

          <input
            type="hidden"
            name="quantity"
            value="1"
          >

          <button
            type="submit"
            name="add"
            class="nd-cross-sell-card__button"
            data-nd-cross-sell-add
            ${
              variant.available
                ? ''
                : 'disabled'
            }
          >
            <span
              data-nd-cross-sell-add-text
            >
              ${
                variant.available
                  ? this.escapeHtml(
                      buttonText
                    )
                  : 'Agotado'
              }
            </span>
          </button>

        </form>

      </div>
    `;

    return article;
  }
    /* =======================================================
     GALLERY
  ======================================================= */

  getActiveGalleryIndex() {
    if (!this.galleryItems.length) {
      return -1;
    }

    return this.galleryItems.findIndex(
      (item) =>
        item.classList.contains(
          'is-active'
        )
    );
  }

  navigateGallery(direction) {
    if (
      !this.galleryItems.length
      || this.galleryItems.length <= 1
    ) {
      return;
    }

    let currentIndex =
      this.getActiveGalleryIndex();

    if (currentIndex < 0) {
      currentIndex = 0;
    }

    let nextIndex =
      currentIndex + direction;

    if (
      nextIndex
      >= this.galleryItems.length
    ) {
      nextIndex = 0;
    }

    if (nextIndex < 0) {
      nextIndex =
        this.galleryItems.length - 1;
    }

    const nextItem =
      this.galleryItems[nextIndex];

    const mediaId =
      nextItem?.dataset?.mediaId;

    if (!mediaId) {
      return;
    }

    this.activateMedia(
      mediaId,
      {
        scrollMobile: false
      }
    );
  }

  updateGalleryCounter(
    mediaId
  ) {
    const index =
      this.galleryItems.findIndex(
        (item) =>
          String(
            item.dataset.mediaId
          )
          === String(
            mediaId
          )
      );

    if (index < 0) {
      return;
    }

    const position =
      index + 1;

    if (
      this.galleryDesktopCounter
    ) {
      this.galleryDesktopCounter.textContent =
        String(
          position
        );
    }

    if (
      this.mobileCounter
    ) {
      this.mobileCounter.textContent =
        String(
          position
        );
    }
  }

  /* =======================================================
     GALLERY LIGHTBOX
  ======================================================= */

  openGalleryLightbox(
    mediaId,
    trigger = null
  ) {
    if (
      !this.galleryLightbox
      || !this.galleryLightboxItems.length
    ) {
      return;
    }

    const activeGalleryIndex =
      this.getActiveGalleryIndex();

    const fallbackMediaId =
      activeGalleryIndex >= 0
        ? this.galleryItems[
            activeGalleryIndex
          ]?.dataset?.mediaId
        : null;

    const normalizedId =
      String(
        mediaId
        || fallbackMediaId
        || ''
      );

    if (!normalizedId) {
      return;
    }

    const matchingItem =
      this.galleryLightboxItems.find(
        (item) =>
          String(
            item.dataset.mediaId
          )
          === normalizedId
      );

    if (!matchingItem) {
      return;
    }

    this.galleryLightboxLastTrigger =
      trigger
      || document.activeElement;

    this.activateGalleryLightboxMedia(
      normalizedId
    );

    this.galleryLightbox.hidden =
      false;

    this.galleryLightbox.setAttribute(
      'aria-hidden',
      'false'
    );

    this.galleryLightbox.classList.add(
      'is-open'
    );

    document.documentElement.classList.add(
      'nd-lightbox-open'
    );

    document.body.classList.add(
      'nd-lightbox-open'
    );

    document.addEventListener(
      'keydown',
      this.handleGalleryLightboxKeydownBound
    );

    requestAnimationFrame(
      () => {
        const closeButton =
          this.galleryLightbox.querySelector(
            '.nd-gallery-lightbox__close'
          );

        closeButton?.focus();
      }
    );
  }

  closeGalleryLightbox() {
    if (
      !this.galleryLightbox
      || this.galleryLightbox.hidden
    ) {
      return;
    }

    this.galleryLightbox.classList.remove(
      'is-open'
    );

    this.galleryLightbox.setAttribute(
      'aria-hidden',
      'true'
    );

    document.documentElement.classList.remove(
      'nd-lightbox-open'
    );

    document.body.classList.remove(
      'nd-lightbox-open'
    );

    document.removeEventListener(
      'keydown',
      this.handleGalleryLightboxKeydownBound
    );

    window.setTimeout(
      () => {
        if (
          this.galleryLightbox
          && !this.galleryLightbox
            .classList
            .contains(
              'is-open'
            )
        ) {
          this.galleryLightbox.hidden =
            true;
        }
      },
      180
    );

    const lastTrigger =
      this.galleryLightboxLastTrigger;

    this.galleryLightboxLastTrigger =
      null;

    if (
      lastTrigger
      && typeof lastTrigger.focus
        === 'function'
    ) {
      window.setTimeout(
        () => {
          lastTrigger.focus();
        },
        0
      );
    }
  }

  getActiveGalleryLightboxIndex() {
    if (
      !this.galleryLightboxItems.length
    ) {
      return -1;
    }

    return this.galleryLightboxItems.findIndex(
      (item) =>
        item.classList.contains(
          'is-active'
        )
    );
  }

  activateGalleryLightboxMedia(
    mediaId
  ) {
    if (!mediaId) {
      return;
    }

    const normalizedId =
      String(
        mediaId
      );

    let activeIndex =
      -1;

    this.galleryLightboxItems.forEach(
      (
        item,
        index
      ) => {
        const active =
          String(
            item.dataset.mediaId
          )
          === normalizedId;

        item.classList.toggle(
          'is-active',
          active
        );

        item.setAttribute(
          'aria-hidden',
          active
            ? 'false'
            : 'true'
        );

        if (active) {
          activeIndex =
            index;
        }
      }
    );

    if (
      activeIndex >= 0
      && this.galleryLightboxCounter
    ) {
      const activeItem =
        this.galleryLightboxItems[
          activeIndex
        ];

      const position =
        Number(
          activeItem.dataset
            .mediaPosition
        );

      this.galleryLightboxCounter.textContent =
        String(
          Number.isFinite(
            position
          )
          && position > 0
            ? position
            : activeIndex + 1
        );
    }
  }

  navigateGalleryLightbox(
    direction
  ) {
    if (
      !this.galleryLightboxItems.length
      || this.galleryLightboxItems.length <= 1
    ) {
      return;
    }

    let currentIndex =
      this.getActiveGalleryLightboxIndex();

    if (currentIndex < 0) {
      currentIndex =
        0;
    }

    let nextIndex =
      currentIndex + direction;

    if (
      nextIndex
      >= this.galleryLightboxItems.length
    ) {
      nextIndex =
        0;
    }

    if (
      nextIndex < 0
    ) {
      nextIndex =
        this.galleryLightboxItems.length
        - 1;
    }

    const nextItem =
      this.galleryLightboxItems[
        nextIndex
      ];

    const mediaId =
      nextItem
        ?.dataset
        ?.mediaId;

    if (!mediaId) {
      return;
    }

    this.activateGalleryLightboxMedia(
      mediaId
    );
  }

  handleGalleryLightboxKeydown(
    event
  ) {
    if (
      !this.galleryLightbox
      || this.galleryLightbox.hidden
    ) {
      return;
    }

    if (
      event.key === 'Escape'
    ) {
      event.preventDefault();

      this.closeGalleryLightbox();

      return;
    }

    if (
      event.key === 'ArrowLeft'
    ) {
      event.preventDefault();

      this.navigateGalleryLightbox(
        -1
      );

      return;
    }

    if (
      event.key === 'ArrowRight'
    ) {
      event.preventDefault();

      this.navigateGalleryLightbox(
        1
      );
    }
  }

  /* =======================================================
     INITIAL GALLERY
  ======================================================= */

  syncInitialGallery(
    variant
  ) {
    if (
      variant
        ?.featuredMediaId
    ) {
      this.activateMedia(
        String(
          variant
            .featuredMediaId
        ),
        {
          scrollMobile: false
        }
      );

      return;
    }

    const activeItem =
      this.querySelector(
        '[data-nd-gallery-item].is-active'
      );

    if (
      activeItem
        ?.dataset
        .mediaId
    ) {
      this.updateGalleryCounter(
        activeItem
          .dataset
          .mediaId
      );
    }
  }

  /* =======================================================
     ACTIVATE MEDIA
  ======================================================= */

  activateMedia(
    mediaId,
    {
      scrollMobile = true
    } = {}
  ) {
    if (!mediaId) {
      return;
    }

    const normalizedId =
      String(
        mediaId
      );

    this.galleryItems.forEach(
      (item) => {
        const active =
          String(
            item.dataset
              .mediaId
          )
          === normalizedId;

        item.classList.toggle(
          'is-active',
          active
        );
      }
    );

    this.galleryThumbs.forEach(
      (thumb) => {
        const active =
          String(
            thumb.dataset
              .mediaId
          )
          === normalizedId;

        thumb.classList.toggle(
          'is-active',
          active
        );

        thumb.setAttribute(
          'aria-current',
          active
            ? 'true'
            : 'false'
        );
      }
    );

    this.updateGalleryCounter(
      normalizedId
    );

    if (
      this.galleryLightbox
      && !this.galleryLightbox.hidden
    ) {
      this.activateGalleryLightboxMedia(
        normalizedId
      );
    }

    const mobileIndex =
      this.mobileSlides.findIndex(
        (slide) =>
          String(
            slide.dataset
              .mediaId
          )
          === normalizedId
      );

    if (
      mobileIndex >= 0
    ) {
      this.updateMobileCounter(
        mobileIndex + 1
      );

      if (
        scrollMobile
        && this.mobileTrack
      ) {
        this.mobileTrack.scrollTo(
          {
            left:
              this.mobileSlides[
                mobileIndex
              ].offsetLeft,

            behavior:
              'smooth'
          }
        );
      }
    }
  }

  /* =======================================================
     MOBILE GALLERY
  ======================================================= */

  handleMobileGalleryScroll() {
    if (
      !this.mobileTrack
      || !this.mobileSlides.length
    ) {
      return;
    }

    if (
      this.galleryScrollFrame
    ) {
      cancelAnimationFrame(
        this.galleryScrollFrame
      );
    }

    this.galleryScrollFrame =
      requestAnimationFrame(
        () => {
          const trackLeft =
            this.mobileTrack
              .scrollLeft;

          let closestIndex =
            0;

          let closestDistance =
            Infinity;

          this.mobileSlides.forEach(
            (
              slide,
              index
            ) => {
              const distance =
                Math.abs(
                  slide.offsetLeft
                  - trackLeft
                );

              if (
                distance
                < closestDistance
              ) {
                closestDistance =
                  distance;

                closestIndex =
                  index;
              }
            }
          );

          this.updateMobileCounter(
            closestIndex + 1
          );

          this.galleryScrollFrame =
            null;
        }
      );
  }

  updateMobileCounter(
    position
  ) {
    if (
      this.mobileCounter
    ) {
      this.mobileCounter.textContent =
        String(
          position
        );
    }
  }

  updateMobileCounterForMedia(
    mediaId
  ) {
    const index =
      this.mobileSlides.findIndex(
        (slide) =>
          String(
            slide.dataset
              .mediaId
          )
          === String(
            mediaId
          )
      );

    if (
      index >= 0
    ) {
      this.updateMobileCounter(
        index + 1
      );
    }
  }

  /* =======================================================
     SIZE GUIDE
  ======================================================= */

  initializeSizeGuides() {
    if (
      !Array.isArray(
        this.sizeGuides
      )
      || !this.sizeGuides.length
    ) {
      return;
    }

    this.sizeGuides.forEach(
      (guide) => {
        const openButton =
          guide.querySelector(
            '[data-nd-size-guide-open]'
          );

        const modal =
          guide.querySelector(
            '[data-nd-size-guide-modal]'
          );

        if (
          !openButton
          || !modal
        ) {
          return;
        }

        /*
         * Guardamos la referencia original del modal.
         * Cuando se abra será movido a <body>.
         */
        guide._ndSizeGuideModal =
          modal;

        guide._ndSizeGuidePlaceholder =
          null;

        openButton.addEventListener(
          'click',
          () => {
            this.openSizeGuide(
              guide,
              openButton
            );
          }
        );

        /*
         * Los listeners se agregan ahora.
         * Aunque posteriormente movamos el modal a <body>,
         * los listeners permanecen asociados a los nodos.
         */
        const closeButtons = [
          ...modal.querySelectorAll(
            '[data-nd-size-guide-close]'
          )
        ];

        closeButtons.forEach(
          (button) => {
            button.addEventListener(
              'click',
              () => {
                this.closeSizeGuide(
                  guide
                );
              }
            );
          }
        );
      }
    );

    this.handleSizeGuideKeydownBound =
      this.handleSizeGuideKeydown.bind(
        this
      );

    document.addEventListener(
      'keydown',
      this.handleSizeGuideKeydownBound
    );

    this.updateSizeGuideSelection();
  }

  /* =======================================================
     SIZE GUIDE — OPEN / PORTAL
  ======================================================= */

  openSizeGuide(
    guide,
    trigger = null
  ) {
    if (!guide) {
      return;
    }

    const modal =
      guide._ndSizeGuideModal
      || guide.querySelector(
        '[data-nd-size-guide-modal]'
      );

    if (!modal) {
      return;
    }

    if (
      this.activeSizeGuide
      && this.activeSizeGuide
        !== guide
    ) {
      this.closeSizeGuide(
        this.activeSizeGuide,
        {
          restoreFocus: false
        }
      );
    }

    /*
     * =====================================================
     * PORTAL
     *
     * Sacamos el modal del stacking context del PDP/Horizon
     * y lo montamos directamente dentro de <body>.
     * =====================================================
     */

    if (
      modal.parentNode
      !== document.body
    ) {
      const placeholder =
        document.createComment(
          'Nodara Size Guide Modal'
        );

      modal.parentNode.insertBefore(
        placeholder,
        modal
      );

      guide._ndSizeGuidePlaceholder =
        placeholder;

      document.body.appendChild(
        modal
      );
    }

    this.activeSizeGuide =
      guide;

    this.sizeGuideLastTrigger =
      trigger
      || document.activeElement;

    this.updateSizeGuideSelection(
      guide
    );

    modal.hidden =
      false;

    modal.setAttribute(
      'aria-hidden',
      'false'
    );

    modal.classList.add(
      'is-open'
    );

    document.documentElement.classList.add(
      'nd-size-guide-open'
    );

    document.body.classList.add(
      'nd-size-guide-open'
    );

    requestAnimationFrame(
      () => {
        this.scrollSelectedSizeGuideCard(
          guide
        );

        const closeButton =
          modal.querySelector(
            '.nd-size-guide-modal__close'
          );

        closeButton?.focus();
      }
    );
  }

  /* =======================================================
     SIZE GUIDE — CLOSE
  ======================================================= */

  closeSizeGuide(
    guide,
    {
      restoreFocus = true
    } = {}
  ) {
    if (!guide) {
      return;
    }

    const modal =
      guide._ndSizeGuideModal
      || guide.querySelector(
        '[data-nd-size-guide-modal]'
      );

    if (!modal) {
      return;
    }

    modal.classList.remove(
      'is-open'
    );

    modal.setAttribute(
      'aria-hidden',
      'true'
    );

    modal.hidden =
      true;

    document.documentElement.classList.remove(
      'nd-size-guide-open'
    );

    document.body.classList.remove(
      'nd-size-guide-open'
    );

    if (
      this.activeSizeGuide
      === guide
    ) {
      this.activeSizeGuide =
        null;
    }

    if (
      restoreFocus
      && this.sizeGuideLastTrigger
      && typeof
        this.sizeGuideLastTrigger
          .focus
        === 'function'
    ) {
      const trigger =
        this.sizeGuideLastTrigger;

      this.sizeGuideLastTrigger =
        null;

      requestAnimationFrame(
        () => {
          trigger.focus();
        }
      );
    } else if (
      !restoreFocus
    ) {
      this.sizeGuideLastTrigger =
        null;
    }
  }

  /* =======================================================
     SIZE GUIDE — KEYBOARD
  ======================================================= */

  handleSizeGuideKeydown(
    event
  ) {
    if (
      event.key !== 'Escape'
    ) {
      return;
    }

    if (
      !this.activeSizeGuide
    ) {
      return;
    }

    event.preventDefault();

    this.closeSizeGuide(
      this.activeSizeGuide
    );
  }

  /* =======================================================
     SIZE GUIDE — SELECTED SIZE
  ======================================================= */

  updateSizeGuideSelection(
    guide = null
  ) {
    if (
      !Array.isArray(
        this.sizeGuides
      )
      || !this.sizeGuides.length
    ) {
      return;
    }

    const selectedOptions =
      this.getSelectedOptions()
        .filter(
          Boolean
        );

    if (
      !selectedOptions.length
    ) {
      return;
    }

    const selectedSize =
      this.normalizeSizeGuideValue(
        selectedOptions[0]
      );

    if (!selectedSize) {
      return;
    }

    const guides =
      guide
        ? [guide]
        : this.sizeGuides;

    guides.forEach(
      (currentGuide) => {
        const modal =
          currentGuide
            ._ndSizeGuideModal
          || currentGuide.querySelector(
            '[data-nd-size-guide-modal]'
          );

        const root =
          modal
          || currentGuide;

        const cards = [
          ...root.querySelectorAll(
            '[data-nd-size-guide-card]'
          )
        ];

        cards.forEach(
          (card) => {
            const cardSize =
              this.normalizeSizeGuideValue(
                card.dataset
                  .sizeValue
              );

            const active =
              cardSize
              === selectedSize;

            card.classList.toggle(
              'is-selected',
              active
            );

            card.setAttribute(
              'aria-current',
              active
                ? 'true'
                : 'false'
            );

            const badge =
              card.querySelector(
                '[data-nd-size-guide-selected]'
              );

            if (badge) {
              badge.hidden =
                !active;
            }
          }
        );
      }
    );
  }

  /* =======================================================
     SIZE GUIDE — NORMALIZE
  ======================================================= */

  normalizeSizeGuideValue(
    value
  ) {
    if (
      value == null
    ) {
      return '';
    }

    return String(
      value
    )
      .toLowerCase()
      .trim()
      .replace(
        /\s+/g,
        ''
      )
      .replace(
        /cm/g,
        ''
      )
      .replace(
        /x/g,
        '×'
      );
  }

  /* =======================================================
     SIZE GUIDE — MOBILE SCROLL
  ======================================================= */

  scrollSelectedSizeGuideCard(
    guide
  ) {
    if (!guide) {
      return;
    }

    const modal =
      guide._ndSizeGuideModal
      || guide.querySelector(
        '[data-nd-size-guide-modal]'
      );

    if (!modal) {
      return;
    }

    const grid =
      modal.querySelector(
        '.nd-size-guide-info-grid'
      );

    const selectedCard =
      modal.querySelector(
        '.nd-size-guide-info-card.is-selected'
      );

    if (
      !grid
      || !selectedCard
    ) {
      return;
    }

    if (
      !window.matchMedia(
        '(max-width: 749px)'
      ).matches
    ) {
      return;
    }

    requestAnimationFrame(
      () => {
        grid.scrollTo(
          {
            left:
              selectedCard.offsetLeft
              - grid.offsetLeft,

            behavior:
              'auto'
          }
        );
      }
    );
  }
    /* =======================================================
     HELPERS
  ======================================================= */

  formatMoney(
    cents
  ) {
    const value =
      Number(
        cents
      ) / 100;

    const currency =
      window.Shopify
        ?.currency
        ?.active
      || 'CLP';

    try {
      return new Intl.NumberFormat(
        'es-CL',
        {
          style:
            'currency',

          currency,

          maximumFractionDigits:
            currency === 'CLP'
              ? 0
              : 2
        }
      ).format(
        value
      );
    } catch {
      return value
        .toLocaleString(
          'es-CL'
        );
    }
  }

  escapeHtml(
    value
  ) {
    const div =
      document.createElement(
        'div'
      );

    div.textContent =
      String(
        value
        ?? ''
      );

    return div.innerHTML;
  }

  dispatchVariantChange(
    variant
  ) {
    this.dispatchEvent(
      new CustomEvent(
        'nodara:variant-change',
        {
          bubbles:
            true,

          detail: {
            variant
          }
        }
      )
    );
  }

  /* =======================================================
     SIZE GUIDE — RESTORE PORTAL
  ======================================================= */

  restoreSizeGuidePortal(
    guide
  ) {
    if (!guide) {
      return;
    }

    const modal =
      guide._ndSizeGuideModal;

    const placeholder =
      guide._ndSizeGuidePlaceholder;

    if (
      modal
      && placeholder
      && placeholder.parentNode
    ) {
      placeholder.parentNode.insertBefore(
        modal,
        placeholder
      );

      placeholder.remove();
    }

    guide._ndSizeGuidePlaceholder =
      null;
  }

  /* =======================================================
     CLEANUP
  ======================================================= */

  disconnectedCallback() {
    /* =====================================================
       STICKY — MAIN CTA
    ===================================================== */

    if (
      this.stickyObserver
    ) {
      this.stickyObserver
        .disconnect();

      this.stickyObserver =
        null;
    }

    /* =====================================================
       STICKY — FOOTER
    ===================================================== */

    if (
      this.stickyFooterObserver
    ) {
      this.stickyFooterObserver
        .disconnect();

      this.stickyFooterObserver =
        null;
    }

    this.stickyMainCtaPassed =
      false;

    this.stickyFooterVisible =
      false;

    this.stickyFooter =
      null;

    if (
      this.stickyAtc
    ) {
      this.stickyAtc
        .classList
        .remove(
          'is-visible'
        );

      this.stickyAtc
        .setAttribute(
          'aria-hidden',
          'true'
        );
    }

    /* =====================================================
       MOBILE GALLERY
    ===================================================== */

    if (
      this.galleryScrollFrame
    ) {
      cancelAnimationFrame(
        this.galleryScrollFrame
      );

      this.galleryScrollFrame =
        null;
    }

    /* =====================================================
       GALLERY LIGHTBOX
    ===================================================== */

    if (
      this.handleGalleryLightboxKeydownBound
    ) {
      document.removeEventListener(
        'keydown',
        this.handleGalleryLightboxKeydownBound
      );

      this.handleGalleryLightboxKeydownBound =
        null;
    }

    document.documentElement
      .classList
      .remove(
        'nd-lightbox-open'
      );

    document.body
      .classList
      .remove(
        'nd-lightbox-open'
      );

    /* =====================================================
       SIZE GUIDE
    ===================================================== */

    if (
      this.handleSizeGuideKeydownBound
    ) {
      document.removeEventListener(
        'keydown',
        this.handleSizeGuideKeydownBound
      );

      this.handleSizeGuideKeydownBound =
        null;
    }

    document.documentElement
      .classList
      .remove(
        'nd-size-guide-open'
      );

    document.body
      .classList
      .remove(
        'nd-size-guide-open'
      );

    /*
     * Si algún modal fue movido a <body>,
     * lo devolvemos a su posición original.
     *
     * Esto es especialmente importante
     * dentro del Shopify Theme Editor.
     */

    if (
      Array.isArray(
        this.sizeGuides
      )
    ) {
      this.sizeGuides.forEach(
        (guide) => {
          this.restoreSizeGuidePortal(
            guide
          );

          guide._ndSizeGuideModal =
            null;

          guide._ndSizeGuidePlaceholder =
            null;
        }
      );
    }

    this.activeSizeGuide =
      null;

    this.sizeGuideLastTrigger =
      null;

    /* =====================================================
       COMPRA V2
    ===================================================== */

    if (
      this.addToCartResetTimer
    ) {
      clearTimeout(
        this.addToCartResetTimer
      );

      this.addToCartResetTimer =
        null;
    }

    if (
      this.handleCartUpdateBound
    ) {
      document.removeEventListener(
        'cart:update',
        this.handleCartUpdateBound
      );

      this.handleCartUpdateBound =
        null;
    }

    if (
      this.handleCartErrorBound
    ) {
      document.removeEventListener(
        'cart:error',
        this.handleCartErrorBound
      );

      this.handleCartErrorBound =
        null;
    }

    if (
      this.handleProductFormSuccessBound
      && this.productFormComponent
    ) {
      this.productFormComponent
        .removeEventListener(
          'product-form:success',
          this.handleProductFormSuccessBound
        );

      this.handleProductFormSuccessBound =
        null;
    }

    if (
      this.handleProductFormErrorBound
      && this.productFormComponent
    ) {
      this.productFormComponent
        .removeEventListener(
          'product-form:error',
          this.handleProductFormErrorBound
        );

      this.handleProductFormErrorBound =
        null;
    }
  }
}

/* =========================================================
   REGISTER NODARA PRODUCT
========================================================= */

if (
  !customElements.get(
    'nd-product'
  )
) {
  customElements.define(
    'nd-product',
    NodaraProduct
  );
}